/* eslint-disable no-bitwise */

import { ComplianceCategory } from '@sonar-software/sonar-core';
import { enumNumericValues } from '@sonar-software/sonar-ts-lib';
import { noop } from 'lodash';

import { COPY_GENERIC, COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { FilterOption, OptionFilter } from '@periscopes/general/filters';
import React, { useCallback, useMemo } from 'react';

const enumOptions = enumNumericValues(ComplianceCategory);

function getOptions(includeAny: boolean): Array<FilterOption> {
	return enumOptions
		.filter((category: ComplianceCategory) => (includeAny ? true : category !== ComplianceCategory.Any))
		.map((category: ComplianceCategory) => ({
			label: ComplianceCategory[ category ],
			value: category
		}));
}

function getValues(value: number | undefined | null): Array<number> {
	if (value === undefined || value === null) {
		return [];
	}

	const results = [];

	for (const option of enumOptions.filter((category: ComplianceCategory) => category !== ComplianceCategory.Any)) {
		if (value & option) {
			results.push(option);
		}
	}

	return results.length ? results : [ 0 ];
}

export interface ComplianceCategoryBulkSelectorProps {
	id?: string;
	includeAny?: boolean;
	mode?: 'multiple' | 'tags';
	onChange?: (value: number | undefined) => void;
	placeholder?: string;
	value?: number;
}

export const ComplianceCategoryFormSelector: React.FC<ComplianceCategoryBulkSelectorProps> = ({
	includeAny = false,
	mode,
	onChange = noop,
	placeholder,
	value,
	...props
}) => {
	const options = useMemo(() => getOptions(includeAny), [ includeAny ]);

	const handleChange = useCallback((change: Array<number>) => {
		if (change[ change.length - 1 ] === ComplianceCategory.None) {
			onChange(ComplianceCategory.None);
		} else {
			const multipleValue = change.length ? change.reduce((prev, curr) => curr + prev, 0) : undefined;

			onChange(mode === 'multiple' ? multipleValue : change);
		}
	}, [ mode, onChange ]);

	return (
		<OptionFilter
			{ ...props }
			mode={ mode }
			onChange={ handleChange }
			options={ options }
			placeholder={ placeholder ?? COPY_GENERIC.SELECT(COPY_SPECIFIC.COMPLIANCE_CATEGORIES) }
			value={ getValues(value) } />
	);
};
